import type { SessionResponse } from '@chatapp-inbox/shared';
import type { Query, QueryClient } from '@tanstack/react-query';
import { setCsrfToken } from '../api';
import type { EndReason, FlagsStore } from './flags-store';
import { SESSION_KEY } from './session';

const isNotSession = (query: Query): boolean => query.queryKey[0] !== SESSION_KEY[0];

/**
 * Esquece tudo que pertence ao usuário anterior: consultas (canceladas antes de removidas, para
 * uma resposta atrasada não repovoar o cache) e mutações (guardam as variáveis enviadas).
 * A query da sessão fica de fora: ela é quem diz quem está conectado.
 *
 * `keepMounted`: a tela continua aberta para outra pessoa (outra aba trocou a conta). Remover do
 * cache uma consulta que ainda tem observador a deixa "solta": o componente segue mostrando os
 * dados antigos e nunca pede os novos. Por isso as consultas em uso são ZERADAS e refeitas (agora
 * com o cookie da pessoa nova), e só as sem uso são removidas. Sem isso (sessão encerrada), as
 * telas desmontam logo em seguida e remover basta.
 */
export function purgeUserData(
  queryClient: QueryClient,
  { keepMounted = false }: { keepMounted?: boolean } = {},
): void {
  void queryClient.cancelQueries({ predicate: isNotSession });
  if (keepMounted) {
    queryClient.removeQueries({ predicate: (query) => isNotSession(query) && !query.isActive() });
    void queryClient.resetQueries({
      predicate: (query) => isNotSession(query) && query.isActive(),
    });
  } else {
    queryClient.removeQueries({ predicate: isNotSession });
  }
  queryClient.getMutationCache().clear();
}

export function endSession(queryClient: QueryClient, store: FlagsStore, reason: EndReason): void {
  // O motivo é gravado antes de a sessão virar null: quem renderizar "sem sessão" já o encontra.
  store.set({ endReason: reason });
  setCsrfToken(null);
  purgeUserData(queryClient);
  queryClient.setQueryData(SESSION_KEY, null);
}

/**
 * Adota `session` como a sessão desta aba (login, ou outra pessoa assumindo o cookie em outra aba).
 * O token CSRF dela já foi guardado por quem a obteve (`login`/`fetchSession`).
 */
export function startSession(
  queryClient: QueryClient,
  store: FlagsStore,
  session: SessionResponse,
): void {
  store.set({ endReason: null, loggingOut: false, logoutError: null });
  // Defesa extra: nada de outro usuário pode sobreviver na mesma aba.
  purgeUserData(queryClient, { keepMounted: true });
  queryClient.setQueryData(SESSION_KEY, session);
}
